import { useEffect, useMemo, useState } from 'react';
import { Copy, Check, FileDown, Mail } from 'lucide-react';
import {
  Card,
  Button,
  Input,
  FormField,
  UniversalPage,
  useToast,
} from '@afrilink/ui';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useStaffRole } from '@/shared/hooks/useStaffRole';
import {
  buildEmailSignatureHtml,
  buildEmailSignatureText,
  buildEmailSignatureDocument,
} from '@/shared/lib/emailSignature';

/**
 * SalesProfilePage — /sales/profile.
 *
 * The user menu has had a "Profile settings" item since the sales app shipped,
 * wired to `/* settings pending *\/`. This is that page, and its first job is
 * the standard email signature.
 *
 * The fields are prefilled from the signed-in account and editable, but NOT
 * persisted. A job title is not a system role — an agent signs off as "Sales
 * Consultant", never as `sales_agent` — and storing one would need a column,
 * a migration and an RLS policy for something the agent pastes into their mail
 * client once. Generate, paste, done. If it changes, generate again.
 */

/** System role -> a title a customer should actually read. Starting point only. */
const TITLE_BY_ROLE: Record<string, string> = {
  sales_agent: 'Sales Consultant',
  sales_manager: 'Sales Manager',
  support: 'Client Support',
  staff: 'Client Services',
  admin: 'Director',
  super_admin: 'Director',
};

export default function SalesProfilePage() {
  const { user } = useAuth();
  const { staffRole } = useStaffRole();
  const { toast } = useToast();

  const [fullName, setFullName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [phone, setPhone] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const meta = (user?.user_metadata ?? {}) as Record<string, unknown>;
    setFullName(((meta.full_name as string) ?? '').trim());
    setPhone(((meta.phone as string) ?? '').trim());
  }, [user]);

  useEffect(() => {
    if (staffRole && !jobTitle) setJobTitle(TITLE_BY_ROLE[staffRole] ?? 'Client Services');
    // Only seeds the initial value — never overwrites what the agent typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffRole]);

  const input = useMemo(
    () => ({ fullName, jobTitle, email: user?.email ?? '', phone }),
    [fullName, jobTitle, user?.email, phone],
  );

  const html = useMemo(() => buildEmailSignatureHtml(input), [input]);

  /**
   * Copy as BOTH text/html and text/plain. Pasting into Gmail or Outlook picks
   * up the html flavour and lands formatted; a plain-text composer gets the
   * fallback instead of a wall of tags. `navigator.clipboard.write` is blocked
   * in insecure contexts and unsupported in some mobile browsers, so the
   * download stays as the route that always works.
   */
  async function copyRich() {
    try {
      const item = new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([buildEmailSignatureText(input)], { type: 'text/plain' }),
      });
      await navigator.clipboard.write([item]);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
      toast({
        tone: 'success',
        title: 'Signature copied',
        body: 'Paste it into your mail client’s signature setting.',
      });
    } catch {
      toast({
        tone: 'warning',
        title: 'Clipboard blocked',
        body: 'Your browser would not allow a formatted copy. Use Download instead.',
      });
    }
  }

  function download() {
    const blob = new Blob([buildEmailSignatureDocument(input)], {
      type: 'text/html;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const slug = (fullName || 'signature').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-');
    a.download = `SightHost-Email-Signature-${slug || 'signature'}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <UniversalPage title="Profile" description="Your details and standard email signature">
      <Card className="p-4 sm:p-5">
        <div className="flex flex-col gap-1">
          <h2 className="inline-flex items-center gap-2 text-base font-semibold">
            <Mail className="h-4 w-4 text-primary" aria-hidden />
            Standard email signature
          </h2>
          <p className="text-sm text-muted-foreground">
            One signature for everyone at SightHost, so a prospect sees the same
            company whoever emails them. Fill in your details, then copy it
            straight into Gmail or Outlook &mdash; or download it if your
            browser blocks the copy.
          </p>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <FormField label="Full name" htmlFor="sig-name">
            <Input
              id="sig-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Orapeleng Banda"
              autoComplete="name"
            />
          </FormField>
          <FormField label="Job title" htmlFor="sig-title">
            <Input
              id="sig-title"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="Sales Consultant"
            />
          </FormField>
          <FormField
            label="Direct / mobile (optional)"
            htmlFor="sig-phone"
            helper="Leave blank to show the switchboard only."
          >
            <Input
              id="sig-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="072 000 0000"
              autoComplete="tel"
            />
          </FormField>
        </div>

        <p className="mt-1 text-xs text-muted-foreground">
          Signing-in email <span className="font-medium text-foreground">{user?.email}</span> is
          used as the address in the signature. These fields are not saved &mdash;
          they only build the block below.
        </p>

        <div className="mt-4">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Preview
          </span>
          {/* The signature is trusted, first-party HTML built in this bundle by
              buildEmailSignatureHtml — never user-supplied markup. Field values
              are escaped in the builder before they reach it. */}
          <div
            className="mt-2 rounded border border-border bg-card p-5 overflow-x-auto"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button onClick={copyRich}>
            {copied ? <Check className="h-4 w-4" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />}
            {copied ? 'Copied' : 'Copy signature'}
          </Button>
          <Button variant="secondary" onClick={download}>
            <FileDown className="h-4 w-4" aria-hidden />
            Download (.html)
          </Button>
        </div>

        <div className="mt-4 rounded border border-border bg-muted/30 p-3">
          <p className="text-xs font-semibold text-foreground">How to install it</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
            <li>
              <span className="font-medium">Gmail:</span> Settings &rarr; See all settings &rarr;
              General &rarr; Signature &rarr; paste.
            </li>
            <li>
              <span className="font-medium">Outlook:</span> File &rarr; Options &rarr; Mail &rarr;
              Signatures &rarr; paste.
            </li>
            <li>
              <span className="font-medium">Apple Mail:</span> Settings &rarr; Signatures &rarr;
              paste, and untick &ldquo;Always match my default message font&rdquo;.
            </li>
          </ul>
          <p className="mt-2 text-xs text-muted-foreground">
            Do not add banking details to your signature. Send the banking
            details letter from the dashboard instead &mdash; a signature is
            forwarded and quoted everywhere, which makes it the easiest thing
            for someone to alter in a fake reply.
          </p>
        </div>
      </Card>
    </UniversalPage>
  );
}
